/**
 * dev-toolbox.ts — v3M13 Dev Toolbox panel.
 *
 * Bundles dev affordances behind a single backtick-toggled overlay:
 *   - Detection rings (mirrors F9, `ui.devDetectionOverlay`)
 *   - Enemy POV swap (mirrors F10, `ui.enemyPovOverlay`)
 *   - Reveal all (`ui.devRevealAll`) — bypasses FoW in renderers + VFX
 *   - +10 000 per resource (Credits / Minerals / Fuel / Research /
 *     Diplomatic / All) against the player empire, clamped at caps
 *
 * Strictly local UI state for the visibility flags — same MULTIPLAYER §2
 * reasoning as F9/F10 (zero command-stream side effects). Resource
 * grants directly mutate GameState, so they're a single-player cheat
 * path; v3M9 multiplayer will need a command path with server
 * arbitration.
 *
 * Mount pattern mirrors `research-panel.ts`: backdrop + panel created
 * once at session start, `update(state)` called per frame to keep
 * checkbox + label state synced, `destroy()` on session teardown.
 */

import { grantAllResourcesToPlayer, grantResourceToPlayer } from '../game/dev-grant';
import type { EmpireId, GameState, RaceId } from '../state/types/index';
import { enterDevSpawnMode, exitDevSpawnMode, type UiState } from './state';

export interface DevToolboxHandle {
  update(state: GameState): void;
  destroy(): void;
}

export interface DevToolboxOptions {
  uiState: UiState;
}

export function mountDevToolbox(
  parent: HTMLElement,
  options: DevToolboxOptions,
): DevToolboxHandle {
  const { uiState } = options;

  // ── panel root ────────────────────────────────────────────────────────
  const panel = document.createElement('div');
  Object.assign(panel.style, {
    position: 'fixed',
    top: '12px',
    left: '12px',
    width: '260px',
    background: 'rgba(6,10,20,0.92)',
    border: '1px solid rgba(80,110,160,0.5)',
    borderRadius: '4px',
    padding: '10px 12px',
    color: '#d8e4ff',
    fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
    fontSize: '12px',
    lineHeight: '1.5',
    fontVariantNumeric: 'tabular-nums',
    display: 'none',
    zIndex: '800',
    pointerEvents: 'auto',
  } as Partial<CSSStyleDeclaration>);

  // ── header ────────────────────────────────────────────────────────────
  const header = document.createElement('div');
  header.textContent = 'DEV TOOLBOX';
  Object.assign(header.style, {
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.12em',
    color: '#8ea0c8',
    marginBottom: '8px',
    textTransform: 'uppercase',
  } as Partial<CSSStyleDeclaration>);
  panel.appendChild(header);

  const hint = document.createElement('div');
  hint.textContent = 'Press ` to toggle';
  Object.assign(hint.style, {
    fontSize: '10px',
    color: '#5d6b85',
    marginBottom: '12px',
  } as Partial<CSSStyleDeclaration>);
  panel.appendChild(hint);

  // ── visibility section ────────────────────────────────────────────────
  panel.appendChild(makeSectionLabel('Visibility'));

  const detectionRow = makeCheckboxRow('Detection rings (F9)', () => {
    uiState.devDetectionOverlay = !uiState.devDetectionOverlay;
  });
  panel.appendChild(detectionRow.row);

  const povRow = makeCheckboxRow('Enemy POV (F10)', () => {
    uiState.enemyPovOverlay = !uiState.enemyPovOverlay;
  });
  panel.appendChild(povRow.row);

  const revealRow = makeCheckboxRow('Reveal all', () => {
    uiState.devRevealAll = !uiState.devRevealAll;
  });
  panel.appendChild(revealRow.row);

  // ── resources section ─────────────────────────────────────────────────
  const resourceLabel = makeSectionLabel('Player Resources');
  resourceLabel.style.marginTop = '12px';
  panel.appendChild(resourceLabel);

  let lastSeenState: GameState | null = null;
  function withState(fn: (s: GameState) => void): void {
    if (lastSeenState !== null) fn(lastSeenState);
  }

  const grantGrid = document.createElement('div');
  Object.assign(grantGrid.style, {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '6px',
  } as Partial<CSSStyleDeclaration>);
  panel.appendChild(grantGrid);

  grantGrid.appendChild(
    makeGrantButton('+10k Credits', () =>
      withState((s) => grantResourceToPlayer(s, 'credits')),
    ),
  );
  grantGrid.appendChild(
    makeGrantButton('+10k Minerals', () =>
      withState((s) => grantResourceToPlayer(s, 'minerals')),
    ),
  );
  grantGrid.appendChild(
    makeGrantButton('+10k Fuel', () =>
      withState((s) => grantResourceToPlayer(s, 'fuel')),
    ),
  );
  grantGrid.appendChild(
    makeGrantButton('+10k Research', () =>
      withState((s) => grantResourceToPlayer(s, 'research')),
    ),
  );
  grantGrid.appendChild(
    makeGrantButton('+10k Diplomatic', () =>
      withState((s) => grantResourceToPlayer(s, 'diplomatic')),
    ),
  );
  grantGrid.appendChild(
    makeGrantButton('+10k All', () => withState((s) => grantAllResourcesToPlayer(s))),
  );

  // ── spawn-entity section (MFix11.3) ──────────────────────────────────
  const spawnLabel = makeSectionLabel('Spawn Entity');
  spawnLabel.style.marginTop = '12px';
  panel.appendChild(spawnLabel);

  const spawnGrid = document.createElement('div');
  Object.assign(spawnGrid.style, {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '6px',
  } as Partial<CSSStyleDeclaration>);
  panel.appendChild(spawnGrid);

  const empirePicker = makeDropdown('Empire (owner)');
  const racePicker = makeDropdown('Race (catalogue)');
  const chassisPicker = makeDropdown('Ship / Structure');
  spawnGrid.appendChild(empirePicker.row);
  spawnGrid.appendChild(racePicker.row);
  spawnGrid.appendChild(chassisPicker.row);

  const spawnToggle = document.createElement('button');
  spawnToggle.type = 'button';
  spawnToggle.textContent = 'Activate Spawning Mode';
  Object.assign(spawnToggle.style, {
    background: '#0E131C',
    color: '#D7DEEA',
    border: '1px solid #2A3344',
    borderRadius: '2px',
    padding: '6px 8px',
    fontFamily: 'inherit',
    fontSize: '11px',
    letterSpacing: '0.04em',
    cursor: 'pointer',
    marginTop: '4px',
  } as Partial<CSSStyleDeclaration>);
  spawnGrid.appendChild(spawnToggle);

  const spawnStatus = document.createElement('div');
  Object.assign(spawnStatus.style, {
    fontSize: '10px',
    color: '#7e8799',
    marginTop: '4px',
    minHeight: '14px',
  } as Partial<CSSStyleDeclaration>);
  spawnGrid.appendChild(spawnStatus);

  // Empire-change handler cascades to race default. Race-change cascades
  // to chassis list. Chassis-change is a leaf (just updates state).
  empirePicker.select.addEventListener('change', () => {
    if (lastSeenState !== null) {
      const empId = Number(empirePicker.select.value) as EmpireId;
      const emp = lastSeenState.empires.get(empId);
      if (emp) {
        racePicker.select.value = emp.raceId;
        repopulateChassis();
      }
    }
  });
  racePicker.select.addEventListener('change', () => repopulateChassis());

  spawnToggle.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    if (uiState.devSpawnMode !== null) {
      exitDevSpawnMode(uiState);
      return;
    }
    if (lastSeenState === null) return;
    const ownerEmpireId = Number(empirePicker.select.value) as EmpireId;
    const raceId = racePicker.select.value as RaceId;
    const chassis = chassisPicker.select.value;
    if (!chassis) return;
    enterDevSpawnMode(uiState, ownerEmpireId, raceId, chassis);
  });

  function repopulateChassis(): void {
    if (lastSeenState === null) return;
    const raceId = racePicker.select.value as RaceId;
    const prev = chassisPicker.select.value;
    chassisPicker.select.innerHTML = '';
    // Group by ship vs. space-structure for readability. Space
    // structures live in `state.shipTemplates` under the same key
    // pattern but are also enumerated in `structuresData.spaceByKind`
    // for the race-gating check.
    const ships: string[] = [];
    const structures: string[] = [];
    const spaceByKind = lastSeenState.structuresData.spaceByKind;
    for (const [key, tmpl] of lastSeenState.shipTemplates) {
      if (!key.startsWith(`${raceId}:`)) continue;
      const isStructure = spaceByKind[tmpl.chassis] !== undefined;
      if (isStructure) {
        // Filter out structures the race can't build (maxLevelByRace
        // entry === 0) — they exist in the catalogue but are
        // race-gated. Dev tool still spawns them via cross-race;
        // hiding here just matches expected per-race availability.
        const row = spaceByKind[tmpl.chassis]!;
        const maxLevel = row.maxLevelByRace[raceId] ?? 0;
        if (maxLevel > 0) structures.push(tmpl.chassis);
      } else {
        ships.push(tmpl.chassis);
      }
    }
    ships.sort();
    structures.sort();
    if (ships.length > 0) {
      const grp = document.createElement('optgroup');
      grp.label = 'Ships';
      for (const c of ships) {
        const opt = document.createElement('option');
        opt.value = c;
        opt.textContent = c;
        grp.appendChild(opt);
      }
      chassisPicker.select.appendChild(grp);
    }
    if (structures.length > 0) {
      const grp = document.createElement('optgroup');
      grp.label = 'Space Structures';
      for (const c of structures) {
        const opt = document.createElement('option');
        opt.value = c;
        opt.textContent = c;
        grp.appendChild(opt);
      }
      chassisPicker.select.appendChild(grp);
    }
    // Preserve prior selection when possible.
    if (prev && [...chassisPicker.select.options].some((o) => o.value === prev)) {
      chassisPicker.select.value = prev;
    }
  }

  let pickersPopulated = false;
  function populatePickersOnce(state: GameState): void {
    if (pickersPopulated) return;
    // Empires.
    empirePicker.select.innerHTML = '';
    const empires = [...state.empires.values()].sort((a, b) => a.id - b.id);
    for (const emp of empires) {
      const opt = document.createElement('option');
      opt.value = String(emp.id);
      opt.textContent = `${emp.name} (${emp.raceId})`;
      empirePicker.select.appendChild(opt);
    }
    // Races (fixed v1 set; future races would auto-derive from templates).
    racePicker.select.innerHTML = '';
    for (const r of ['RACE-AA', 'RACE-BB', 'RACE-CC']) {
      const opt = document.createElement('option');
      opt.value = r;
      opt.textContent = r;
      racePicker.select.appendChild(opt);
    }
    // Default race = player empire's race.
    const player = state.empires.get(state.playerEmpireId);
    if (player) {
      empirePicker.select.value = String(state.playerEmpireId);
      racePicker.select.value = player.raceId;
    }
    repopulateChassis();
    pickersPopulated = true;
  }

  parent.appendChild(panel);

  // ── update / destroy ──────────────────────────────────────────────────
  function update(state: GameState): void {
    lastSeenState = state;
    panel.style.display = uiState.devToolboxOpen ? 'block' : 'none';
    if (!uiState.devToolboxOpen) return;
    detectionRow.checkbox.checked = uiState.devDetectionOverlay;
    povRow.checkbox.checked = uiState.enemyPovOverlay;
    revealRow.checkbox.checked = uiState.devRevealAll;
    // MFix11.3 — populate empire / race / chassis pickers on first
    // state observed; reflect placement-mode toggle in the button +
    // status text per-frame.
    populatePickersOnce(state);
    const spawnActive = uiState.devSpawnMode !== null;
    spawnToggle.textContent = spawnActive
      ? 'Stop Spawning (Esc)'
      : 'Activate Spawning Mode';
    spawnToggle.style.background = spawnActive ? '#5C2618' : '#0E131C';
    spawnToggle.style.borderColor = spawnActive ? '#A05038' : '#2A3344';
    if (spawnActive) {
      const m = uiState.devSpawnMode!;
      const emp = state.empires.get(m.ownerEmpireId);
      const owner = emp ? emp.name : `#${m.ownerEmpireId}`;
      spawnStatus.textContent = `Click in system view to spawn ${m.chassis} for ${owner}`;
    } else {
      spawnStatus.textContent = '';
    }
  }

  function destroy(): void {
    panel.remove();
  }

  return { update, destroy };
}

// ─── helpers ────────────────────────────────────────────────────────────

function makeSectionLabel(text: string): HTMLElement {
  const el = document.createElement('div');
  el.textContent = text;
  Object.assign(el.style, {
    fontSize: '10px',
    color: '#7e8799',
    letterSpacing: '0.10em',
    textTransform: 'uppercase',
    marginBottom: '6px',
  } as Partial<CSSStyleDeclaration>);
  return el;
}

interface CheckboxRow {
  row: HTMLElement;
  checkbox: HTMLInputElement;
}

function makeCheckboxRow(label: string, onToggle: () => void): CheckboxRow {
  const row = document.createElement('label');
  Object.assign(row.style, {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 0',
    cursor: 'pointer',
    userSelect: 'none',
  } as Partial<CSSStyleDeclaration>);
  const checkbox = document.createElement('input');
  checkbox.type = 'checkbox';
  Object.assign(checkbox.style, {
    accentColor: '#3F6FA8',
  } as Partial<CSSStyleDeclaration>);
  checkbox.addEventListener('change', () => onToggle());
  const text = document.createElement('span');
  text.textContent = label;
  row.appendChild(checkbox);
  row.appendChild(text);
  return { row, checkbox };
}

interface DropdownRow {
  row: HTMLElement;
  select: HTMLSelectElement;
}

function makeDropdown(label: string): DropdownRow {
  const row = document.createElement('label');
  Object.assign(row.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    fontSize: '10px',
    color: '#7e8799',
  } as Partial<CSSStyleDeclaration>);
  const text = document.createElement('span');
  text.textContent = label;
  const select = document.createElement('select');
  Object.assign(select.style, {
    background: '#0E131C',
    color: '#D7DEEA',
    border: '1px solid #2A3344',
    borderRadius: '2px',
    padding: '4px 6px',
    fontFamily: 'inherit',
    fontSize: '11px',
    cursor: 'pointer',
  } as Partial<CSSStyleDeclaration>);
  row.appendChild(text);
  row.appendChild(select);
  return { row, select };
}

function makeGrantButton(label: string, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = label;
  Object.assign(btn.style, {
    background: '#0E131C',
    color: '#D7DEEA',
    border: '1px solid #2A3344',
    borderRadius: '2px',
    padding: '6px 8px',
    fontFamily: 'inherit',
    fontSize: '11px',
    letterSpacing: '0.04em',
    cursor: 'pointer',
  } as Partial<CSSStyleDeclaration>);
  // pointerdown to dodge the rebuild-race noted in detail-panel.ts.
  btn.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    onClick();
  });
  return btn;
}
